'use client';

import React from 'react';
import { INVESTOR_COUNT_CLAIM } from '@/lib/approx';
import Link from 'next/link';
import { ArrowRight } from 'lucide-react';
import { buttonVariants } from '@/components/ui/Button';
import { PaperPlaneAnimation } from '@/components/ui/PaperPlaneAnimation';
import { Tooltip } from '@/components/ui/Tooltip';
import { useTranslation } from 'react-i18next';

const Hero: React.FC = () => {
  const { t } = useTranslation();

  return (
    <section className="relative py-12 md:py-20 overflow-hidden bg-gradient-to-b from-white to-gray-50">
      {/* Aurora / gradient-mesh backdrop (additive, decorative) */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 -z-10 overflow-hidden">
        <div className="absolute -top-24 -left-24 w-[28rem] h-[28rem] rounded-full bg-primary-300/30 blur-3xl" />
        <div className="absolute top-10 right-0 w-[26rem] h-[26rem] rounded-full bg-secondary-300/25 blur-3xl" />
        <div className="absolute bottom-0 left-1/3 w-[22rem] h-[22rem] rounded-full bg-primary-200/30 blur-3xl" />
      </div>
      <div className="container">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-8 items-center">
          <div className="reveal-rise">
            <div className="inline-block bg-primary-50 rounded-full px-4 py-1 text-primary-600 font-medium text-sm mb-6 animate-tagline-pulse">
              {t('pageContent.home.hero.tagline')}
            </div>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-900 leading-tight mb-6">
              {t('pageContent.home.hero.title.prefix')}{' '}
              <span className="text-primary-500 inline-block hover-scale">
                {t('pageContent.home.hero.title.highlight')}
              </span>
            </h1>
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">
              {t('pageContent.home.hero.subtitle')}
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <a
                href="#upload-section"
                className={buttonVariants({ size: 'lg', className: 'w-full sm:w-auto hover-scale' })}
              >
                {t('pageContent.home.hero.cta.upload')}
              </a>
              {/* Secondary CTA is the FREE match preview, not the old
                  "#how-it-works" scroll. Two reasons: it is a real product
                  action rather than a jump down the same page, and it is the
                  only above-the-fold route into /match-preview — which shipped
                  with no inbound links at all. /how-it-works remains reachable
                  from the header nav and the footer. */}
              <Link
                href="/match-preview"
                className={buttonVariants({
                  variant: 'outline',
                  size: 'lg',
                  className: 'w-full sm:w-auto group hover-scale',
                })}
              >
                {t('pageContent.home.hero.cta.matchPreview')}
                <ArrowRight className="ml-2 w-4 h-4 group-hover:translate-x-1 transition-transform" aria-hidden="true" />
              </Link>
            </div>
            <div className="mt-8 grid grid-cols-3 gap-6">
              {[
                // Every figure here must be verifiable. The investor figure is
                // INVESTOR_COUNT_CLAIM — the ONE database number the whole site
                // states, rounded down from 5,087 measured active rows; it is
                // imported rather than typed so this tile cannot drift from the
                // rest of the site (it used to read a hardcoded '5000+', which is
                // the same figure in a different format). "24h" is the
                // twentyFourHourDelivery promise carried by every pricing tier.
                // A "60+ countries" figure used to sit in the middle slot — it was
                // removed because investor location is unnormalised free text with
                // 349 rows carrying none, so no country count has ever been computed.
                // Do not reinstate it, and do not add a firm count or an
                // "Unlimited" tile here either. See lib/approx.ts.
                { number: INVESTOR_COUNT_CLAIM, label: t('pageContent.home.hero.stats.investors'), hint: t('pageContent.home.hero.stats.investorsHint') },
                { number: '24h', label: t('pageContent.home.hero.stats.turnaround'), hint: t('pageContent.home.hero.stats.turnaroundHint') },
                { number: '$19/mo', label: t('pageContent.home.hero.stats.fee'), hint: t('pageContent.home.hero.stats.feeHint') },
              ].map((stat, index) => (
                <Tooltip key={index} content={stat.hint} dark={false}>
                  <div
                    className="text-center p-4 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors cursor-help reveal-rise"
                    style={{ animationDelay: `${(index + 1) * 0.1}s` }}
                  >
                    <div className="text-2xl font-bold text-primary-500 break-words">{stat.number}</div>
                    <div className="text-sm text-gray-500 break-words">{stat.label}</div>
                  </div>
                </Tooltip>
              ))}
            </div>
          </div>
          <div className="relative reveal-fade" style={{ animationDelay: '0.2s' }}>
            <PaperPlaneAnimation className="w-full max-w-md mx-auto hover-lift" />

            {/* This card used to show five hardcoded stars, a fabricated quote
                ("SendVC helped us connect with 3 investors who ended up
                participating in our seed round") and an invented author,
                "Jane D., Founder & CEO". None of it was real, and
                docs/BRAND_ENTITY_KIT.md forbids exactly that: "any customer
                count, rating, or testimonial". It is replaced with three claims
                the product actually delivers. Do not reintroduce social proof
                here until there are real, attributable customers to quote. */}
            <div className="absolute -bottom-10 left-0 right-0 bg-white rounded-lg shadow-lg p-6 mx-8 backdrop-blur-sm bg-white/90 hover-lift reveal-fade" style={{ animationDelay: '0.4s' }}>
              <div className="text-sm font-semibold text-gray-900 mb-3">
                {t('pageContent.home.hero.proof.title')}
              </div>
              <ul className="space-y-2">
                {(t('pageContent.home.hero.proof.items', { returnObjects: true }) as string[]).map(
                  (item) => (
                    <li key={item} className="flex items-start text-sm text-gray-700">
                      <svg
                        className="w-4 h-4 text-primary-600 mr-2 mt-0.5 flex-shrink-0"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                        aria-hidden="true"
                      >
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                      </svg>
                      {item}
                    </li>
                  )
                )}
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export { Hero };